import { resolveDcDefaultRate } from '../../Dashboard/TodoList/CostingTsx/calc/landed';
import { toFiniteNumber } from '../../Dashboard/TodoList/CostingTsx/calc/primitives';
import type { ExchangeRateEntry } from '../../Dashboard/TodoList/CostingTsx/calc/types';

/* Exchange-rate resolution for the Product Costing detail page (BM-9618).

   Pure: no React, no redux, no I/O — so the same code runs in the page and in tests. The master
   lookup itself is reused from the Costing TODO calculation engine (calc/landed.resolveDcDefaultRate)
   so the detail page and the TODO grid cannot drift apart. */

/**
 * A season exchange-rate master entry. Extends the engine's shape with `defaultCurrencyValue`, which
 * the migration moved from the document root onto each entry.
 */
export interface ExchangeRateMasterEntry extends ExchangeRateEntry {
  defaultCurrencyValue?: string | number;
}

/** The per-distribution-centre exchange-rate block stored on each `landedInfoStates` entry. */
export interface LandedExchangeRateInfo {
  exchangedRate?: string | number | undefined;
  exchangedRateManualFalse?: string | number | undefined;
  companyCurrency?: string | undefined;
  supplierCurrency?: string | undefined;
  companyCurrencyValue?: string | number | undefined;
  manualAdded?: boolean | undefined;
  updatedBy?: string | undefined;
  updatedAt?: string | undefined;
}

export interface RebaseExchangeInfoInput {
  dcCurrency?: string;
  supplierCurrency?: string;
  exchangeRateList?: ExchangeRateMasterEntry[];
}

/**
 * Resolves a distribution centre's default exchange rate: the master combination of the DC's own
 * currency against the supplier currency.
 *
 * The two "no rate" cases are deliberately distinct. While the master list is still loading there is
 * nothing to look up, so the caller's fallback (the global primary currency value) is used. Once the
 * list has arrived, a combination that simply is not configured resolves to 1 rather than to another
 * distribution centre's rate.
 *
 * A blank master value parses to 0, which the conversion helpers already treat as "no conversion";
 * collapsing it to 1 keeps the rate input from displaying a misleading 0.
 *
 * @param exchangeRateList the season's exchange-rate master entries.
 * @param dcCurrency the distribution centre's own currency.
 * @param supplierCurrency the currency the supplier prices in.
 * @param loadingFallback rate to use only while the master list is unavailable.
 * @returns the exchange rate, never zero or negative.
 */
export function resolveDcDefaultCurrValue(
  exchangeRateList: ExchangeRateMasterEntry[] | undefined,
  dcCurrency: string | undefined,
  supplierCurrency: string | undefined,
  loadingFallback?: string | number | null,
): number {
  if (!Array.isArray(exchangeRateList) || exchangeRateList.length === 0) {
    return loadingFallback === null || loadingFallback === undefined ? 1 : toFiniteNumber(loadingFallback);
  }
  if (!dcCurrency || !supplierCurrency) return 1;
  const rate = resolveDcDefaultRate({ companyCurrency: dcCurrency, supplierCurrency }, exchangeRateList);
  return rate && rate > 0 ? rate : 1;
}

/**
 * Resolves a distribution centre's own base value from the master entry for its currency.
 *
 * This is the base-currency row of the exchange-rate master, which the master screen renders
 * read-only at 1 — so the result is 1 in practice. It is looked up rather than hardcoded so the
 * value follows the master, and so nothing depends on the document-root `defaultCurrencyValue`
 * field that the multiple-currencies migration removed.
 *
 * @param exchangeRateList the season's exchange-rate master entries.
 * @param dcCurrency the distribution centre's own currency.
 * @returns the base value, defaulting to 1.
 */
export function resolveCompanyCurrencyValue(exchangeRateList: ExchangeRateMasterEntry[] | undefined, dcCurrency: string | undefined): number {
  if (!Array.isArray(exchangeRateList) || exchangeRateList.length === 0 || !dcCurrency) return 1;
  const matched = exchangeRateList.find((entry) => entry?.defaultCurrency === dcCurrency);
  return toFiniteNumber(matched?.defaultCurrencyValue) || 1;
}

/**
 * Rebases a distribution centre's exchange-rate block onto a new supplier currency.
 *
 * Any manual override is cleared: a rate typed against the previous supplier's currency carries no
 * meaning once the supplier changes, so the rate is re-seeded from the master and the manual
 * amendment stamp is dropped along with it.
 *
 * @param exchangeInfo the distribution centre's current exchange-rate block.
 * @param input the distribution centre currency, the new supplier currency and the season master.
 * @returns a new exchange-rate block; the input is not mutated.
 */
export function rebaseExchangeInfoForSupplier(exchangeInfo: LandedExchangeRateInfo | undefined, input: RebaseExchangeInfoInput): LandedExchangeRateInfo {
  const { dcCurrency, supplierCurrency, exchangeRateList } = input;
  const rest = { ...(exchangeInfo || {}) };
  delete rest.updatedBy;
  delete rest.updatedAt;
  return {
    ...rest,
    companyCurrency: dcCurrency,
    supplierCurrency,
    manualAdded: false,
    exchangedRate: resolveDcDefaultCurrValue(exchangeRateList, dcCurrency, supplierCurrency),
    companyCurrencyValue: resolveCompanyCurrencyValue(exchangeRateList, dcCurrency),
  };
}
